// Mirror of /app/backend/audit_data.py — verbatim spec + scoring
export const AUDIT_INTRO = {
  badge: "Money Belief Audit",
  headline: "The 5 hidden beliefs running your money story.",
  subhead:
    "Five quick self-assessments. About six minutes. By the end you'll know the one belief that's been quietly running the show — and exactly how to start rewiring it.",
  body: [
    "Most money problems aren't really money problems. They're belief problems wearing a financial costume.",
    "This audit walks you through the 5 belief systems that shape every financial decision you make:",
  ],
  pillars: [
    "Scarcity vs. Abundance",
    "Worthiness vs. Unworthiness",
    "Control vs. Powerlessness",
    "Growth vs. Fixed Identity",
    "Trust vs. Fear",
  ],
  closing:
    "Awareness is the first step. Then you'll see your reframes — the new beliefs that change everything.",
};

export const BELIEFS = [
  {
    id: 1,
    key: "scarcity",
    title: "Scarcity vs. Abundance",
    tagline: "How safe does money feel to you?",
    statements: [
      "I worry there won't be enough, even when I have savings",
      "I feel guilty spending money on myself",
      "I believe I have to choose between enjoying life now OR saving for later",
      "I think talking about money is uncomfortable",
    ],
    scoring: [
      { min: 15, max: 20, label: "Strong scarcity mindset", interp: "This is likely your biggest barrier." },
      { min: 8, max: 14, label: "Mixed beliefs", interp: "You're in transition." },
      { min: 4, max: 7, label: "Strong abundance mindset", interp: "" },
    ],
    reframe: {
      intro: "Complete this sentence:",
      old_prompt: 'Money is _______________ (perhaps "scarce," "stressful," "never enough")',
      new_prompt: "Now complete it again, but write what you WANT to believe:\nMoney is _______________",
      example: '"Money is a tool that flows to me as I create value, and there\'s enough for both today\'s joy and tomorrow\'s security."',
      practice: 'Every time you catch yourself thinking "I can\'t afford that," reframe to "How can I afford that?" or "I\'m choosing to prioritize X instead of Y right now."',
    },
  },
  {
    id: 2,
    key: "worthiness",
    title: "Worthiness vs. Unworthiness",
    tagline: "Do you believe wealth is for you?",
    statements: [
      "I don't deserve financial success as much as others",
      "I sabotage myself when things start going well financially",
      'I feel like I\'m "playing pretend" at being financially successful',
      "I believe wealth is for other people, not people like me",
    ],
    scoring: [
      { min: 15, max: 20, label: "Deep worthiness blocks", interp: "This belief is holding you back significantly." },
      { min: 8, max: 14, label: "Intermittent unworthiness", interp: "You go back and forth." },
      { min: 4, max: 7, label: "Strong sense of financial worthiness", interp: "" },
    ],
    reframe: {
      intro: "Write down and complete this sentence honestly:",
      old_prompt: '"I don\'t deserve financial success because _______________"',
      new_prompt: "Now challenge it: Is that actually true? Or is that a story you've been telling yourself?\n\nWrite your new belief, something like:",
      example: '"I create value in the world, and money is simply the exchange for that value. I am worthy of receiving it."',
      practice: 'Every time you receive money (paycheck, refund, found $5), say out loud or to yourself: "I receive this with gratitude. I am worthy of financial abundance."',
    },
  },
  {
    id: 3,
    key: "control",
    title: "Control vs. Powerlessness",
    tagline: "Who's actually steering your financial life?",
    statements: [
      'Money just "happens to me" — I don\'t feel in control of it',
      "I avoid looking at my accounts because I feel helpless",
      "External circumstances (economy, job, etc.) control my financial life",
      "I believe there's nothing I can do to change my financial situation",
    ],
    scoring: [
      { min: 15, max: 20, label: "Feeling powerless", interp: "This creates inaction." },
      { min: 8, max: 14, label: "Some sense of control", interp: "But still feel at the mercy of circumstances." },
      { min: 4, max: 7, label: "Strong internal locus of control", interp: "" },
    ],
    reframe: {
      intro: "List 3 financial decisions you made this month (even small ones: bought coffee, paid a bill, put money in savings).",
      old_prompt: "1) _______________\n2) _______________\n3) _______________",
      new_prompt: "This proves that you ARE making financial decisions. You DO have control. You just need to be more intentional about it.\n\nWrite your new belief, something like:",
      example: '"I am in control of my financial decisions. While I can\'t control everything, I control my responses and my choices."',
      practice: 'Before any purchase over $20, pause and ask: "Am I choosing this intentionally, or am I on autopilot?"',
    },
  },
  {
    id: 4,
    key: "growth",
    title: "Growth vs. Fixed Identity",
    tagline: "Do you believe you can actually learn this?",
    statements: [
      '"I\'m just not good with money" is part of who I am',
      "I believe financial skills are innate — you either have them or you don't",
      "I've tried to change my money habits before and failed, so why bother",
      'I think people who are good with money are just "wired differently"',
    ],
    scoring: [
      { min: 15, max: 20, label: "Fixed identity around money", interp: "You believe you can't change." },
      { min: 8, max: 14, label: "Some openness to change", interp: 'But still identify as "bad with money."' },
      { min: 4, max: 7, label: "Growth mindset", interp: "You believe you can learn and improve." },
    ],
    reframe: {
      intro: "Think of ONE skill you didn't have 5 years ago but have now (cooking, Excel, a language, parenting, your job). You learned that. You can learn this too.",
      old_prompt: "What's a skill you've already learned in the last 5 years?\n_______________",
      new_prompt: "Write your new belief, something like:",
      example: '"Being good with money is a skill, not a personality trait. I am capable of learning this skill."',
      practice: 'Replace "I\'m bad with money" with "I\'m learning to manage money." Replace "I can\'t stick to a budget" with "I haven\'t found the right system yet." Language matters.',
    },
  },
  {
    id: 5,
    key: "trust",
    title: "Trust vs. Fear",
    tagline: "Do you trust yourself with money?",
    statements: [
      "I constantly worry about worst-case financial scenarios",
      "I don't trust myself to make good financial decisions",
      "I'm afraid to invest because I might lose everything",
      "I believe financial disaster is always lurking around the corner",
    ],
    scoring: [
      { min: 15, max: 20, label: "Fear-based relationship with money", interp: "This creates paralysis." },
      { min: 8, max: 14, label: "Cautious but not paralyzed", interp: "Some trust, some fear." },
      { min: 4, max: 7, label: "Trust-based relationship", interp: "You believe things will work out." },
    ],
    reframe: {
      intro: "Write down your biggest financial fear:",
      old_prompt: "My biggest financial fear is: _______________\n\nNow write: \"If this happened, I would...\" and list 3 things you could do to handle it. You're more resourceful than you think.\n1) _______________\n2) _______________\n3) _______________",
      new_prompt: "Write your new belief, something like:",
      example: '"I trust myself to handle whatever comes. I make thoughtful decisions, and even if I make mistakes, I can course-correct."',
      practice: "Make one small financial decision you've been avoiding out of fear (check your balance, open that bill, research that investment). Take action despite the fear.",
    },
  },
];

export const RATING_LABELS = ["Never", "Rarely", "Sometimes", "Often", "Always"];

export function bandFor(belief, score) {
  return belief.scoring.find((b) => score >= b.min && score <= b.max) || belief.scoring[belief.scoring.length - 1];
}

// 0 = healthy/green, 1 = mixed/amber, 2 = strong-block/red
export function bucketForBelief(score) {
  if (score <= 7) return 0;
  if (score <= 14) return 1;
  return 2;
}
